import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

const SWIPE_THRESHOLD_PX = 36;
const CARD_STEP_DURATION_MS = 420;

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useSwipeSteppedCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef({
    active: false,
    pointerId: 0,
    pointerType: '',
    startX: 0,
    moved: false,
  });
  const stepFrameRef = useRef(0);
  const suppressClickRef = useRef(false);
  const resumeAfterPointerClickRef = useRef(false);
  const pointerClickTypeRef = useRef('');
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => () => window.cancelAnimationFrame(stepFrameRef.current), []);

  const stepToAdjacentCard = (distance: number): boolean => {
    const track = trackRef.current;
    if (!track || prefersReducedMotion()) return false;

    const animation = track.getAnimations()[0];
    const duration = Number(animation?.effect?.getTiming().duration);
    const cardCount = track.querySelectorAll(
      '[data-carousel-group="original"] [data-carousel-copy="original"]',
    ).length;
    const currentTime = typeof animation?.currentTime === 'number' ? animation.currentTime : 0;
    if (!animation || !Number.isFinite(duration) || duration <= 0 || cardCount === 0) return false;

    window.cancelAnimationFrame(stepFrameRef.current);
    const direction = distance < 0 ? 1 : -1;
    const cardStep = duration / cardCount;
    let startTime = currentTime;
    if (direction < 0 && startTime < cardStep) startTime += duration;
    const targetTime = startTime + (direction * cardStep);

    animation.pause();
    animation.currentTime = startTime;
    setIsInteracting(true);
    let stepStartedAt: number | null = null;

    const animateStep = (timestamp: number) => {
      stepStartedAt ??= timestamp;
      const progress = Math.min((timestamp - stepStartedAt) / CARD_STEP_DURATION_MS, 1);
      const easedProgress = 1 - ((1 - progress) ** 3);
      animation.currentTime = startTime + ((targetTime - startTime) * easedProgress);

      if (progress < 1) {
        stepFrameRef.current = window.requestAnimationFrame(animateStep);
        return;
      }

      animation.updatePlaybackRate(1);
      animation.play();
      setIsInteracting(false);
    };

    stepFrameRef.current = window.requestAnimationFrame(animateStep);
    return true;
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.isPrimary && event.pointerType === 'mouse') {
      resumeAfterPointerClickRef.current = true;
      pointerClickTypeRef.current = 'mouse';
      return;
    }

    const isTouchGesture = event.pointerType === 'touch' || event.pointerType === 'pen';
    if (!event.isPrimary || !isTouchGesture || prefersReducedMotion()) return;

    gestureRef.current = {
      active: true,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      moved: false,
    };
    setIsInteracting(true);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture.active || gesture.pointerId !== event.pointerId) return;

    if (Math.abs(event.clientX - gesture.startX) >= SWIPE_THRESHOLD_PX) {
      gesture.moved = true;
      if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
    }
  };

  const finishGesture = (event: ReactPointerEvent<HTMLDivElement>, shouldStep: boolean) => {
    const gesture = gestureRef.current;
    if (!gesture.active || gesture.pointerId !== event.pointerId) return;

    gesture.active = false;
    const distance = event.clientX - gesture.startX;

    if (shouldStep && (gesture.moved || Math.abs(distance) >= SWIPE_THRESHOLD_PX)) {
      suppressClickRef.current = true;
      if (!stepToAdjacentCard(distance)) setIsInteracting(false);
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      return;
    }

    resumeAfterPointerClickRef.current = gesture.pointerType === 'touch' || gesture.pointerType === 'pen';
    pointerClickTypeRef.current = gesture.pointerType;
    setIsInteracting(false);
  };

  return {
    isInteracting,
    onClickCapture: (event: ReactMouseEvent<HTMLDivElement>) => {
      if (suppressClickRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!resumeAfterPointerClickRef.current) return;
      resumeAfterPointerClickRef.current = false;
      const shouldOverrideHoverPause = pointerClickTypeRef.current === 'mouse';
      pointerClickTypeRef.current = '';
      setIsInteracting(true);
      window.setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        if (!shouldOverrideHoverPause) setIsInteracting(false);
      }, 0);
    },
    onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => finishGesture(event, false),
    onPointerDown,
    onPointerLeave: (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.pointerType === 'mouse') setIsInteracting(false);
    },
    onPointerMove,
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => finishGesture(event, true),
    trackRef,
  };
}
